import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight, ShieldCheck, FileText, ScrollText, MapPin, ClipboardCheck,
  Building2, ScanLine, Phone, CalendarClock, Ruler, Compass, Award
} from "lucide-react";
import { COMPANY } from "@/lib/site";

const HERO_PHOTO =
  "https://images.unsplash.com/photo-1601658797866-7ae55d05a817?fm=jpg&q=85&w=2400&auto=format&fit=crop";

const SERVICE_ICONS = [Building2, ScanLine, MapPin, ClipboardCheck];
const SERVICE_TYPES = ["kuca", "pomocna_zgrada", "gospodarska_zgrada", "drugo"];
const NUMBER_ICONS = [Award, Compass, Ruler];
const WHY_ICONS = [ShieldCheck, FileText, ScrollText, ClipboardCheck];
const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];

export default function LegalizacijaPage() {
  const { t } = useTranslation();
  const stats = t("legalizacija.stats", { returnObjects: true });
  const marquee = t("legalizacija.marquee", { returnObjects: true });
  const numbersRows = t("legalizacija.numbersRows", { returnObjects: true });
  const services = t("legalizacija.services", { returnObjects: true });
  const processSteps = t("legalizacija.processSteps", { returnObjects: true });
  const why = t("legalizacija.why", { returnObjects: true });
  const testimonials = t("legalizacija.testimonials", { returnObjects: true });

  const statsArr = Array.isArray(stats) ? stats : [];
  const marqueeArr = Array.isArray(marquee) ? marquee : [];
  const numbersRowsArr = Array.isArray(numbersRows) ? numbersRows : [];
  const servicesArr = Array.isArray(services) ? services : [];
  const processStepsArr = Array.isArray(processSteps) ? processSteps : [];
  const whyArr = Array.isArray(why) ? why : [];
  const testimonialsArr = Array.isArray(testimonials) ? testimonials : [];

  return (
    <div data-testid="legalizacija-page">
      <Seo title={t("legalizacija.seoTitle")} path="/legalizacija" description={t("legalizacija.seoDesc")} />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full" style={{ background: "radial-gradient(circle, rgba(217,45,32,0.06) 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 right-0 w-[640px] h-[640px] rounded-full" style={{ background: "radial-gradient(circle, rgba(217,45,32,0.04) 0%, transparent 70%)" }} />
          <div className="absolute inset-0 grid-lines opacity-[0.05]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 reveal">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink/30" /> {t("legalizacija.heroKicker")}
              </div>
              <h1 className="font-display text-ink text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold mt-6">
                {t("legalizacija.heroTitle1")}<br />
                <span className="relative inline-block">
                  <span className="text-ink-soft">{t("legalizacija.heroTitle2")}</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#D92D20]" aria-hidden="true" />
                </span><br />
                {t("legalizacija.heroTitle3")}
              </h1>
              <div className="mt-6 inline-flex items-center gap-2 border border-ink/15 bg-white px-3 py-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#D92D20]" strokeWidth={2} />
                <span className="font-brand uppercase text-[11px] tracking-[0.22em] text-ink">
                  {t("legalizacija.heroBadge")}
                </span>
              </div>
              <p className="text-ink-soft text-lg max-w-2xl mt-8 leading-relaxed">
                {t("legalizacija.heroLead")}
                <span className="block mt-3 text-ink/70">
                  {t("legalizacija.heroLeadSub")}
                </span>
              </p>
              <div className="flex flex-wrap gap-3 mt-10">
                <Link
                  to="/prijava"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
                >
                  {t("common.applyNow")}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
                <Link
                  to="/postupak-legalizacije"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-white transition-colors"
                >
                  {t("legalizacija.heroHowItWorks")}
                </Link>
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 text-ink px-2 py-3.5 text-sm font-medium hover:text-[#D92D20] transition-colors"
                  data-testid="hero-phone-link"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.75} />
                  {COMPANY.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 reveal" style={{ animationDelay: "180ms" }}>
              <div className="relative">
                <div className="relative aspect-[4/5] overflow-hidden border border-hair shadow-xl">
                  <img
                    src={HERO_PHOTO}
                    alt={t("legalizacija.splitImgAlt")}
                    className="w-full h-full object-cover"
                    style={{ filter: "saturate(1.08)" }}
                  />
                  <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm px-3.5 py-2.5 border-l-2 border-[#D92D20]">
                    <div className="font-brand uppercase text-[#D92D20] text-[10px] tracking-[0.25em] font-bold">{t("legalizacija.newLaw")}</div>
                    <div className="font-display text-ink text-3xl font-bold tracking-tighter leading-none mt-0.5">
                      2026<span className="text-[#D92D20]">.</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(10,25,47,0.88) 60%, rgba(10,25,47,0.95) 100%)" }}>
                    <div className="font-brand uppercase text-[10px] tracking-[0.25em] text-white/80">{t("legalizacija.legalFramework")}</div>
                    <p className="text-white text-sm leading-snug mt-2">
                      {t("legalizacija.legalFrameworkNote")}
                    </p>
                    <Link
                      to="/zakonski-propisi"
                      className="inline-flex items-center gap-1.5 mt-3 text-white text-xs font-medium border-b border-white/40 pb-0.5 hover:border-white"
                    >
                      {t("legalizacija.readLaws")}
                      <ArrowUpRight className="w-3 h-3" strokeWidth={1.75} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-hair bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
            {statsArr.map((s, i) => (
              <div
                key={s.label}
                className={`py-7 ${i < statsArr.length - 1 ? "md:border-r" : ""} border-hair ${
                  i % 2 === 0 ? "border-r md:border-r" : ""
                }`}
              >
                <div
                  className={`font-display text-4xl sm:text-5xl tracking-tighter font-bold ${
                    i === 0 ? "text-[#D92D20]" : "text-ink"
                  }`}
                >
                  {s.kpi}
                </div>
                <div className="text-ink-soft text-xs uppercase tracking-[0.15em] mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE TICKER */}
      <section className="relative bg-ink text-white overflow-hidden border-b border-ink" aria-hidden="true">
        <div className="flex whitespace-nowrap py-4 marquee-track">
          {[...marqueeArr, ...marqueeArr].map((m, i) => (
            <div key={i} className="flex items-center gap-6 px-8">
              <span className="font-brand uppercase text-sm tracking-[0.2em] text-white/85">{m}</span>
              <span className="text-[#D92D20] text-xl leading-none">◆</span>
            </div>
          ))}
        </div>
        <style>{`
          .marquee-track { animation: marquee 38s linear infinite; }
          @keyframes marquee { to { transform: translateX(-50%); } }
          @media (prefers-reduced-motion: reduce) { .marquee-track { animation: none; } }
        `}</style>
      </section>

      {/* WARM REASSURANCE */}
      <section className="border-b border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[#D92D20]">{t("legalizacija.numbersKicker")}</div>
            <div className="mt-4 flex items-end gap-4 flex-wrap">
              <span className="font-display text-[120px] sm:text-[180px] lg:text-[220px] leading-[0.85] tracking-tighter font-bold text-ink">
                2.000
              </span>
              <span className="font-display text-6xl sm:text-7xl text-[#D92D20] font-bold leading-none">+</span>
            </div>
            <div className="font-brand uppercase tracking-[0.2em] text-ink-soft mt-4">
              {t("legalizacija.numbersLabel")}
            </div>
          </div>
          <div className="md:col-span-5 space-y-5">
            {numbersRowsArr.map((row, i) => {
              const Icon = NUMBER_ICONS[i] || Award;
              return (
                <div key={row.t} className="flex items-start gap-4 bg-white border border-hair p-5 hover-lift">
                  <div className="w-10 h-10 bg-ink text-white flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-ink">{row.t}</div>
                    <div className="text-ink-soft text-sm mt-1 leading-relaxed">{row.d}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENTO services */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="legalizacija-services">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline text-ink-soft">{t("legalizacija.servicesKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              {t("legalizacija.servicesTitle1")}<br />{t("legalizacija.servicesTitle2")}
            </h2>
          </div>
          <div className="md:col-span-5 text-ink-soft leading-relaxed">
            {t("legalizacija.servicesIntro")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-14">
          {servicesArr.map((s, i) => {
            const Icon = SERVICE_ICONS[i] || Building2;
            const span = SPANS[i] || "md:col-span-6";
            const isAccent = i === 0;
            return (
              <Link
                key={s.title}
                to={`/prijava?vrsta=${SERVICE_TYPES[i] || "drugo"}`}
                className={`${span} group border border-hair p-8 lg:p-10 hover-lift bg-white relative overflow-hidden block`}
                data-testid={`service-card-${i}`}
              >
                <div className="absolute top-0 left-0 h-1 w-0 bg-[#D92D20] group-hover:w-full transition-[width] duration-500" />
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 ${isAccent ? "bg-[#D92D20] text-white" : "bg-ink text-white"} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-5xl text-ink/10 font-bold leading-none">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-semibold text-ink tracking-tight mt-10">
                  {s.title}
                </h3>
                <p className="text-ink-soft text-[15px] leading-relaxed mt-3 max-w-md">
                  {s.desc}
                </p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-ink text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                  {t("common.applyNow")}
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="bg-ink text-white border-t border-ink noise relative">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[#F4A8A0]">{t("legalizacija.processKicker")}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05] text-white">
                {t("legalizacija.processTitle1")}<br />{t("legalizacija.processTitle2")}
              </h2>
            </div>
            <div className="md:col-span-5 text-white/75 leading-relaxed">
              {t("legalizacija.processIntro")}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-12">
            {processStepsArr.map((p, i) => {
              const n = `0${i + 1}`;
              return (
                <div key={n} className="bg-ink p-7 relative group">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-5xl tracking-tighter font-bold text-white/15">{n}</span>
                    <span className="h-px flex-1 bg-white/15 group-hover:bg-[#D92D20] transition-colors" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold tracking-tight mt-6 text-white">{p.t}</h3>
                  <p className="text-white/65 text-sm mt-2 leading-relaxed">{p.d}</p>
                  <CalendarClock className="absolute bottom-7 right-7 w-4 h-4 text-white/30" strokeWidth={1.5} />
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <Link
              to="/postupak-legalizacije"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              {t("legalizacija.processDetailCta")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
            <span className="text-white/50 text-sm">{t("common.callSameDay")}</span>
          </div>
        </div>
      </section>

      {/* SPLIT block */}
      <section className="border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="overline text-ink-soft">{t("legalizacija.whyKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              {t("legalizacija.whyTitle1")}<br />
              <span className="text-[#D92D20]">{t("legalizacija.whyTitle2")}</span>
            </h2>
            <p className="text-ink-soft mt-6 leading-relaxed max-w-lg">
              {t("legalizacija.whyLead")}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 mt-10 border border-ink/10">
              {whyArr.map((b, i) => {
                const Icon = WHY_ICONS[i] || ShieldCheck;
                return (
                  <div key={b.t} className="bg-white p-6">
                    <Icon className="w-5 h-5 text-ink" strokeWidth={1.5} />
                    <div className="font-display font-semibold text-ink mt-4">{b.t}</div>
                    <div className="text-ink-soft text-sm mt-1.5">{b.d}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-hair">
              <img
                src="https://images.unsplash.com/photo-1691310939842-bcec99e88aa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtZWRpdGVycmFuZWFuJTIwc3RvbmUlMjBob3VzZSUyMGNyb2F0aWF8ZW58MHx8fHwxNzgyNDY1NzEzfDA&ixlib=rb-4.1.0&q=85"
                alt={t("legalizacija.splitImgAlt")}
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-1.5 font-brand uppercase text-[10px] tracking-[0.2em] text-ink">
                {t("legalizacija.splitImgBadge")}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#D92D20] text-white p-6 max-w-xs shadow-xl">
              <div className="overline text-white/70">{t("legalizacija.locationLabel")}</div>
              <div className="font-display text-2xl mt-2 leading-tight">{COMPANY.address}</div>
              <div className="text-white/80 text-sm mt-2">{t("legalizacija.locationNote")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <div className="overline text-[#D92D20]">{t("legalizacija.testimonialsKicker")}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                {t("legalizacija.testimonialsTitle1")}<br />{t("legalizacija.testimonialsTitle2")}
              </h2>
            </div>
            <div className="md:col-span-5 text-ink-soft leading-relaxed">
              {t("legalizacija.testimonialsIntro")}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonialsArr.map((q, i) => (
              <div key={i} className="bg-white border border-hair p-7 hover-lift relative">
                <div className="absolute -top-3 left-6 font-display text-6xl text-[#D92D20] leading-none">“</div>
                <p className="text-ink leading-relaxed text-[15px] mt-3">{q.t}</p>
                <div className="mt-6 pt-5 border-t border-hair">
                  <div className="font-display font-semibold text-ink">{q.a}</div>
                  <div className="text-ink-soft text-xs">{q.m}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BIG CONTACT CTA */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-[#F4A8A0]">{t("legalizacija.contactKicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05]">
              {t("legalizacija.contactTitle1")}<br />{t("legalizacija.contactTitle2")}
            </h2>
            <p className="text-white/75 mt-5 leading-relaxed max-w-xl">
              {t("legalizacija.contactLead")}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/prijava"
                data-testid="legalizacija-bottom-cta"
                className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
              >
                {t("common.applyNow")}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
              <a
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.75} />
                {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 border-t md:border-t-0 md:border-l border-white/15 md:pl-10 pt-6 md:pt-0">
            <div className="overline text-white/50">{t("common.directContact")}</div>
            <a href={`mailto:${COMPANY.email}`} className="block font-display text-xl mt-3 hover:text-white/80 break-all">
              {COMPANY.email}
            </a>
            <div className="text-white/60 text-sm mt-4">{COMPANY.hours}</div>
            <div className="text-white/50 text-xs mt-3 leading-relaxed">
              {t("legalizacija.contactNote")}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
